import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    searchUserEventsByUserAndDate,
    searchUsersAppointmentsAndSlotsByDate
} from '../../../store/calendarService';
import { searchUsersModulesByDate } from '../../../store/courseService';
import {
    loadAppointmentTypeDetails,
    loadLocationIdsAndNames,
    loadServiceDetailsByService
} from '../../../store/directusService';
import { loadTeamsForService } from '../../../store/userService';
import { addEmailAddressAsNameToArray } from '../../../utils/userArrayUtils';
import { hasRole } from '../../../utils/userRoles';
import MultiSelect from '../../formElements/MultiSelect';
import SingleSelect from '../../formElements/SingleSelect';
import Switch from '../../formElements/Switch';
import { acceptedRoles } from '../utils/calendarUtils';

import SchedulingAssistantAdviserPanel from './SchedulingAssistantAdviserPanel';
import SchedulingAssistantCalendarManagement from './SchedulingAssistantCalendarManagement';

import screen from '../styles/schedulingAssistant.module.css';

const SchedulingAssistant = () => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [service, setService] = useState({});
    const [teamsList, setTeamsList] = useState([]);
    const [selectedTeams, setSelectedTeams] = useState([]);
    const [isAppointmentSlots, setIsAppointmentSlots] = useState(false);
    const [advisers, setAdvisers] = useState([]);
    const [selectedAdvisers, setSelectedAdvisers] = useState([]);
    const [activeAdviser, setActiveAdviser] = useState('');
    const [currentDateView, setCurrentDateView] = useState('');

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const users = useSelector((state) => state.entities.userService.usersByLoggedInUserServiceIds);
    const teamsForService = useSelector((state) => state.entities.userService.teamsForService);
    const appointmentTypes = useSelector(
        (state) => state.entities.directusService.appointmentTypeDetails
    );
    const services = useSelector((state) => state.entities.directusService.serviceDetails);
    const locations = useSelector((state) => state.entities.directusService.locationIdsAndNames);
    const appointmentsAndSlotsByUserAndDateMetaData = useSelector(
        (state) => state.entities.calendarService.appointmentsAndSlotsByUserAndDateMetaData
    );
    const loadingAppointmentsAndSlotsByUserAndDate = useSelector(
        (state) => state.entities.calendarService.loadingAppointmentsAndSlotsByUserAndDate
    );
    const courseModulesByUsersAndDateMetaData = useSelector(
        (state) => state.entities.courseService.courseModulesByUsersAndDateMetaData
    );
    const loadingCourseModulesByUsersAndDate = useSelector(
        (state) => state.entities.courseService.loadingCourseModulesByUsersAndDate
    );
    const userEventsByUserAndDateMetaData = useSelector(
        (state) => state.entities.calendarService.userEventsByUserAndDateMetaData
    );
    const loadingUserEventsByUserAndDate = useSelector(
        (state) => state.entities.calendarService.loadingUserEventsByUserAndDate
    );

    // USE EFFECTS
    useEffect(() => {
        if (!appointmentTypes?.length) dispatch(loadAppointmentTypeDetails());
        if (locations?.length < 1) dispatch(loadLocationIdsAndNames());
    }, []);

    useEffect(() => {
        if (!('serviceIds' in loggedInUser)) return;
        dispatch(loadServiceDetailsByService(loggedInUser.serviceIds));
    }, [loggedInUser.id]);

    useEffect(() => {
        setTeamsList(teamsForService);
    }, [teamsForService]);

    useEffect(() => {
        if (!users.length) return;
        let advisers;
        if (selectedTeams.length) {
            const selectedTeamIds = selectedTeams.map((el) => el.id);
            advisers = users.filter((el) =>
                [el.primaryTeam?.id, ...el.otherTeams.map((entry) => entry.id)]
                    .filter((entry) => entry)
                    .some((entry) => selectedTeamIds.includes(entry))
            );
        } else {
            advisers = users.filter((el) =>
                [el.primaryService, ...el.otherServices].includes(service.id)
            );
        }
        setAdvisers(addEmailAddressAsNameToArray(advisers));
        setSelectedAdvisers([]);
    }, [selectedTeams, service.id, users]);

    useEffect(() => {
        if (
            !loadingCourseModulesByUsersAndDate &&
            'number' in courseModulesByUsersAndDateMetaData &&
            !courseModulesByUsersAndDateMetaData.last
        ) {
            const userIds = selectedAdvisers.map((el) => el.id);
            userIds.length > 0 &&
                dispatch(
                    searchUsersModulesByDate(
                        {
                            userIds,
                            date: currentDateView
                        },
                        courseModulesByUsersAndDateMetaData.number + 1
                    )
                );
        }
    }, [courseModulesByUsersAndDateMetaData, loadingCourseModulesByUsersAndDate]);

    useEffect(() => {
        if (
            !loadingAppointmentsAndSlotsByUserAndDate &&
            'number' in appointmentsAndSlotsByUserAndDateMetaData &&
            !appointmentsAndSlotsByUserAndDateMetaData.last
        ) {
            const userIds = selectedAdvisers.map((el) => el.id);
            userIds.length > 0 &&
                dispatch(
                    searchUsersAppointmentsAndSlotsByDate(
                        {
                            userIds: selectedAdvisers.map((el) => el.id),
                            date: currentDateView
                        },
                        appointmentsAndSlotsByUserAndDateMetaData.number + 1
                    )
                );
        }
    }, [appointmentsAndSlotsByUserAndDateMetaData, loadingAppointmentsAndSlotsByUserAndDate]);

    useEffect(() => {
        if (
            !loadingUserEventsByUserAndDate &&
            'number' in userEventsByUserAndDateMetaData &&
            !userEventsByUserAndDateMetaData.last
        ) {
            const userIds = selectedAdvisers.map((el) => el.id);
            userIds.length > 0 &&
                dispatch(
                    searchUserEventsByUserAndDate(
                        {
                            userIds: selectedAdvisers.map((el) => el.id),
                            date: currentDateView
                        },
                        userEventsByUserAndDateMetaData.number + 1
                    )
                );
        }
    }, [userEventsByUserAndDateMetaData, loadingUserEventsByUserAndDate]);

    useEffect(() => {
        onLoadAppointmentsAndModules(selectedAdvisers.map((el) => el.id));
    }, [currentDateView]);

    // FNS
    const onLoadAppointmentsAndModules = (userIds) => {
        if (userIds.length < 1) return;

        dispatch(
            searchUsersModulesByDate(
                {
                    userIds,
                    date: currentDateView
                },
                0
            )
        );

        dispatch(
            searchUsersAppointmentsAndSlotsByDate(
                {
                    userIds,
                    date: currentDateView
                },
                0
            )
        );

        dispatch(
            searchUserEventsByUserAndDate(
                {
                    userIds,
                    date: currentDateView
                },
                0
            )
        );
    };

    // EVENT HANDLERS
    const onServiceChange = (chosenId) => {
        if (chosenId === service?.id) return;
        if (chosenId) {
            setService(services.find((el) => el.id === chosenId) || {});
            dispatch(loadTeamsForService({ serviceIds: [chosenId] }));
        } else setService({});
        setTeamsList([]);
        setSelectedTeams([]);
    };

    const onTeamsChange = (chosenIds) => {
        const teams = teamsList.filter((el) => chosenIds.includes(el.id));
        setSelectedTeams(teams);
    };

    const onUpdateSelectedAdvisers = async (chosenIds) => {
        !chosenIds.includes(activeAdviser.id) && setActiveAdviser('');
        await setSelectedAdvisers(chosenIds.map((el) => advisers.find((entry) => entry.id === el)));
        if (chosenIds.length > selectedAdvisers.length) onLoadAppointmentsAndModules(chosenIds);
    };

    // RENDER
    return (
        <div className={screen.scheduler}>
            <div className={screen.schedulerToolbar}>
                <SingleSelect
                    id="ServiceId"
                    label="Service"
                    placeholder="Select a service"
                    disabled={!hasRole(acceptedRoles, roles)}
                    mandatory={true}
                    customClass="absolutePosition"
                    menuItems={services || []}
                    selectedId={service?.id || ''}
                    selected={service}
                    onChange={(id) => onServiceChange(id)}
                />

                <MultiSelect
                    id="teamIds"
                    label="Teams"
                    placeholder="Select teams"
                    disabled={!service?.id || !hasRole(acceptedRoles, roles)}
                    mandatory={true}
                    customClass="absolutePosition"
                    menuItems={teamsList || []}
                    preSelectedIds={selectedTeams.map((el) => el.id) || []}
                    preSelects={selectedTeams || []}
                    onChange={(ids) => onTeamsChange(ids)}
                />
                <Switch
                    id="appointmentSlotsSwitch"
                    leftLabel="Appointment"
                    rightLabel="Appointment Slots"
                    subLabelClass="formLabel"
                    disabled={!hasRole(acceptedRoles, roles)}
                    checked={isAppointmentSlots}
                    customClass="verticalAlignCenter"
                    onChange={(e) => setIsAppointmentSlots(e.target.checked)}
                />
            </div>

            <div className={screen.timeviewContentContainer}>
                <div className={screen.schedulingSidebar}>
                    <SchedulingAssistantAdviserPanel
                        advisers={advisers}
                        selectedAdvisers={selectedAdvisers}
                        activeAdviser={activeAdviser}
                        removeSelectedAdviser={setSelectedAdvisers}
                        onUpdateSelectedAdvisers={onUpdateSelectedAdvisers}
                        onChangeActiveAdviser={setActiveAdviser}
                        disabled={!service?.id || selectedTeams.length < 1}
                    />
                </div>
                <div className={screen.timeviewCalendarContent}>
                    <SchedulingAssistantCalendarManagement
                        selectedAdvisers={selectedAdvisers}
                        selectedAdviser={activeAdviser}
                        isAppointmentSlots={isAppointmentSlots}
                        onGetCurrentDate={setCurrentDateView}
                        currentDateView={currentDateView}
                    />
                </div>
            </div>
        </div>
    );
};

export default SchedulingAssistant;
